import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBLEforAquapilotWeb,
  UUID_CHAR_WIFI_SSID,
  UUID_WIFI_SERVICE,
} from "./useBle";
import { useCreateDealerDeviceLink } from "./useDevices";
import { trackEvent, upgradeSession } from "utils/analytics";
import { notify } from "utils/notify";
import { extractSerialFromBleName } from "utils/bleDeviceName";
import { validateEmail } from "components/add-device/wizardValidation";
import { createWizardBleHandlers } from "components/add-device/wizardBleHandlers";

interface UseAddDeviceWizardArgs {
  onComplete: (deviceName: string | undefined) => void;
  onHide: () => void;
}

export function useAddDeviceWizard({
  onComplete,
  onHide,
}: UseAddDeviceWizardArgs) {
  const [activeStep, setActiveStep] = useState(0);
  const [skipScan, setSkipScan] = useState(false);
  const [wifiSSID, setWifiSSID] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [deviceSerialNumber, setDeviceSerialNumber] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const hasHandledConnectionRef = useRef(false);

  const queryClient = useQueryClient();
  const createDeviceLinkMutation = useCreateDealerDeviceLink();

  const {
    scanForLifeguard,
    connectToDevice,
    disconnectFromDevice,
    sendWifiCredentials,
    connectedDevice,
    deviceDisplayName,
    isConnected,
    loading,
    readWifiSSID,
  } = useBLEforAquapilotWeb();

  const resolvedDeviceName =
    deviceDisplayName || connectedDevice?.name || undefined;

  useEffect(() => {
    if (!deviceDisplayName) return;
    const serial = extractSerialFromBleName(deviceDisplayName);
    if (serial) setDeviceSerialNumber(serial);
  }, [deviceDisplayName]);

  useEffect(() => {
    if (!connectedDevice || !isConnected) {
      hasHandledConnectionRef.current = false;
      return;
    }

    if (activeStep !== 1 || hasHandledConnectionRef.current) return;

    hasHandledConnectionRef.current = true;
    notify.success(
      `Device ${resolvedDeviceName || "unknown"} successfully connected!`,
    );
    setActiveStep(2);

    const serial = extractSerialFromBleName(resolvedDeviceName);
    if (serial) setDeviceSerialNumber(serial);

    if (!connectedDevice.gatt) return;

    setStatusMessage("Reading current WiFi configuration...");
    setProcessing(true);
    readWifiSSID(connectedDevice.gatt, UUID_WIFI_SERVICE, UUID_CHAR_WIFI_SSID)
      .then((ssid) => {
        if (ssid) {
          setWifiSSID(ssid);
          notify.info(`Current WiFi SSID: ${ssid}`);
        }
      })
      .catch((error) => {
        console.error("Error reading WiFi SSID:", error);
      })
      .finally(() => {
        setProcessing(false);
        setStatusMessage("");
      });
  }, [
    connectedDevice,
    isConnected,
    activeStep,
    readWifiSSID,
    resolvedDeviceName,
  ]);

  const { handleScan, handleConnect, handleConfigureWifi } =
    createWizardBleHandlers({
      scanForLifeguard,
      connectToDevice,
      sendWifiCredentials,
      connectedDevice,
      deviceDisplayName: resolvedDeviceName,
      isConnected,
      wifiSSID,
      wifiPassword,
      setSkipScan,
      setProcessing,
      setStatusMessage,
      setActiveStep,
    });

  const handleSerialNumberChange = (value: string) => {
    setDeviceSerialNumber(value.replace(/\D/g, "").slice(0, 8));
  };

  const handleEmailChange = (value: string) => {
    setClientEmail(value);
    setIsEmailValid(validateEmail(value));
  };

  const handleLinkDevice = async () => {
    if (!deviceSerialNumber) {
      notify.warning("Serial number not detected. Please enter it manually.");
      return;
    }
    if (!validateEmail(clientEmail)) {
      notify.warning("Please enter a valid client email address.");
      return;
    }

    setStatusMessage(`Linking device ${deviceSerialNumber} to your account...`);
    const prefix =
      process.env.REACT_APP_ENV === "production" ? "dwlg" : "dlgt";

    try {
      const result = await createDeviceLinkMutation.mutateAsync({
        serialNumber: `${prefix}-${deviceSerialNumber}`,
        clientAuthorizerEmail: clientEmail,
        skippedBle: skipScan,
      });

      if (!result.success) {
        throw new Error(
          result.response?.message || "Failed to link the device",
        );
      }

      notify.success(
        `Device ${deviceSerialNumber} successfully linked to your account!`,
      );
      trackEvent("device_linked", { serial_number: deviceSerialNumber });
      upgradeSession("device_linked");
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      setActiveStep(4);
    } catch (error: any) {
      console.error("Error linking device:", error);
      notify.error(
        error.response?.data?.message ||
          "Failed to link the device to your account.",
      );
    }
  };

  const handleFinish = () => {
    setStatusMessage("Completing the configuration...");
    trackEvent("wizard_completed");
    disconnectFromDevice();
    setActiveStep(0);
    notify.success(
      "The device has been successfully configured and is ready to use!",
    );
    onComplete(deviceSerialNumber || resolvedDeviceName);
    onHide();
  };

  const handleSkipScan = () => {
    setSkipScan(true);
    trackEvent("wizard_scan_skipped");
    setActiveStep(3);
  };

  return {
    activeStep,
    setActiveStep,
    skipScan,
    wifiSSID,
    setWifiSSID,
    wifiPassword,
    setWifiPassword,
    deviceSerialNumber,
    clientEmail,
    isEmailValid,
    processing,
    statusMessage,
    loading,
    isConnected,
    connectedDevice,
    deviceDisplayName: resolvedDeviceName,
    createDeviceLinkMutation,
    handleScan,
    handleConnect,
    handleConfigureWifi,
    handleSerialNumberChange,
    handleEmailChange,
    handleLinkDevice,
    handleFinish,
    handleSkipScan,
  };
}
